import type { Prompt } from "./types";

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function normalizePromptText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function extractPromptTokens(value: string): string[] {
  return normalizePromptText(value)
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
}

export interface NormalizedPromptChoiceOption {
  value: string;
  label: string;
  description?: string;
}

function parseJsonPromptChoiceOption(
  option: string,
): Record<string, unknown> | null {
  const trimmed = option.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return asRecord(parsed);
  } catch {
    return null;
  }
}

export function normalizePromptChoiceOption(
  option: string | { value?: string; label?: string; description?: string },
): NormalizedPromptChoiceOption {
  if (typeof option === "string") {
    const record = parseJsonPromptChoiceOption(option);
    if (!record) {
      return { value: option, label: option };
    }
    const value =
      typeof record.value === "string"
        ? record.value
        : typeof record.id === "string"
          ? record.id
          : typeof record.label === "string"
            ? record.label
            : JSON.stringify(record);
    return {
      value,
      label: typeof record.label === "string" ? record.label : value,
      description:
        typeof record.description === "string" ? record.description : undefined,
    };
  }

  const record = option;
  if (!record) {
    return { value: "", label: "" };
  }
  const nestedJson =
    (typeof record.value === "string"
      ? parseJsonPromptChoiceOption(record.value)
      : null) ||
    (typeof record.label === "string"
      ? parseJsonPromptChoiceOption(record.label)
      : null);
  if (nestedJson) {
    return normalizePromptChoiceOption(nestedJson);
  }

  const value =
    typeof record.value === "string"
      ? record.value
      : typeof record.label === "string"
        ? record.label
        : JSON.stringify(record);
  return {
    value,
    label: typeof record.label === "string" ? record.label : value,
    description:
      typeof record.description === "string" ? record.description : undefined,
  };
}

export function scorePromptChoiceMatch(
  answer: string,
  answerTokens: string[],
  option: string | { value?: string; label?: string; description?: string },
): { score: number; resolvedValue: string | null } {
  const choice = normalizePromptChoiceOption(option);
  const { value, label } = choice;
  const description = choice.description || "";
  const haystack = normalizePromptText(
    [value, label, description].filter(Boolean).join(" "),
  );
  if (!haystack) return { score: 0, resolvedValue: value || label || null };

  let score = 0;
  if (value && normalizePromptText(value) === answer) score += 12;
  if (label && normalizePromptText(label) === answer) score += 10;
  if (answer.includes(haystack)) score += 8;
  if (haystack.includes(answer)) score += 6;
  for (const token of answerTokens) {
    if (value && normalizePromptText(value).includes(token)) score += 10;
    if (label && normalizePromptText(label).includes(token)) score += 8;
    if (description && normalizePromptText(description).includes(token))
      score += 5;
  }
  return { score, resolvedValue: value || label || null };
}

export function normalizePromptType(
  raw: Record<string, unknown> | null | undefined,
): Prompt["type"] {
  const type = typeof raw?.type === "string" ? raw.type : null;
  const kind = typeof raw?.kind === "string" ? raw.kind : null;
  const promptType =
    typeof raw?.promptType === "string" ? raw.promptType : null;
  if (type === "confirm" || type === "choice" || type === "input") return type;
  if (kind === "confirm" || kind === "choice" || kind === "input") return kind;
  if (
    promptType === "confirm" ||
    promptType === "choice" ||
    promptType === "input"
  )
    return promptType;
  return "input";
}

export function normalizePrompt(rawValue: unknown): Prompt | null {
  const raw = asRecord(rawValue);
  if (!raw) return null;
  const promptRecord = asRecord(raw.prompt);
  const source = promptRecord || raw;
  const id =
    typeof source.id === "string"
      ? source.id
      : typeof raw.id === "string"
        ? raw.id
        : typeof raw.promptId === "string"
          ? raw.promptId
          : "";
  if (!id) return null;

  return {
    id,
    type: normalizePromptType(source === raw ? raw : { ...raw, ...source }),
    title: typeof source.title === "string" ? source.title : "Input required",
    message: typeof source.message === "string" ? source.message : "",
    options: Array.isArray(source.options)
      ? (source.options.map((option) =>
          typeof option === "string" || asRecord(option)
            ? normalizePromptChoiceOption(
                option as
                  | string
                  | { value?: string; label?: string; description?: string },
              )
            : option,
        ) as Prompt["options"])
      : undefined,
    defaultValue: source.defaultValue,
    placeholder:
      typeof source.placeholder === "string" ? source.placeholder : undefined,
    allowEmpty:
      typeof source.allowEmpty === "boolean" ? source.allowEmpty : undefined,
    metadata: asRecord(source.metadata) || undefined,
  };
}

export function resolvePromptAnswer(
  prompt: Prompt | undefined,
  answer: unknown,
): unknown {
  if (!prompt) return answer;
  if (prompt.type === "confirm") {
    if (typeof answer === "boolean") return answer;
    if (typeof answer === "string")
      return /^(yes|y|true|confirm|ok)/i.test(answer.trim());
    return Boolean(answer);
  }
  if (
    prompt.type === "choice" &&
    Array.isArray(prompt.options) &&
    typeof answer === "string"
  ) {
    const normalized = normalizePromptText(answer);
    const tokens = extractPromptTokens(answer);
    let best: { score: number; resolvedValue: string | null } = {
      score: -1,
      resolvedValue: null,
    };
    for (const option of prompt.options) {
      const scored = scorePromptChoiceMatch(normalized, tokens, option);
      if (scored.score > best.score) best = scored;
    }
    if (best.resolvedValue) return best.resolvedValue;
  }
  return answer;
}
